import OnboardBgWrapper from '@/components/smartai/backgroundImage/OnboardBgWrapper';
import OnboardingPageHeader from '@/components/smartai/ComponentOnboarding/OnboardingPageHeader';
import OnboardingProjectHeader from '@/components/smartai/ComponentOnboarding/OnboardingProjectHeader';
import { Progress } from '@/components/ui/progress';
import { Spinner } from '@/components/ui/spinner';

interface BatchProgress {
  currentBatch: number;
  totalBatches: number;
  percentage: number;
  processedFiles: number;
  totalFiles: number;
}

export interface ProcessFilesContentProps {
  filesCount: number;
  repoUrl: string;
  branchName: string;
  processing: boolean;
  progress: BatchProgress;
  elapsedTime: string;
  error: string | null;
  org: string;
  project: string;
  repoName: string;
  embedded?: boolean;
}

const ProcessFilesContent = ({
  branchName,
  processing,
  progress,
  elapsedTime,
  error,
  org,
  project,
  repoName,
  embedded = false,
}: ProcessFilesContentProps) => {
  const content = (
    <div
      className={
        embedded
          ? 'box-border w-full px-8 pb-8 pt-8 max-[520px]:px-5'
          : 'box-border min-h-[calc(100vh-3.5rem)] w-full max-w-[1200px] mx-auto px-7 pb-12 pt-8 max-[768px]:px-4 max-[768px]:pb-8 max-[768px]:pt-6'
      }>
      <OnboardingPageHeader
        eyebrow={embedded ? 'Add Repository' : 'Onboarding'}
        title="Scanning your repository"
        subtitle="Running AST analysis on selected files. This may take a few minutes for large repositories."
      />

      <OnboardingProjectHeader
        org={org}
        project={project}
        repoName={repoName}
        branchName={branchName}
      />

      {error ? (
        <div
          role="alert"
          className="mb-4 rounded-[10px] border border-solid border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
          {error}
        </div>
      ) : null}

      {processing ? (
        <div className="mt-4 rounded-[14px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] p-8 text-center shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]">
          <div className="mb-6 flex items-center justify-center gap-3">
            <Spinner compact />
            <h3 className="m-0 text-lg font-semibold text-[var(--inv-text)]">
              Processing Files…
            </h3>
          </div>

          <div className="mb-4 flex flex-col items-center">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--inv-text-muted)]">
              Time Elapsed
            </span>
            <span className="mt-1 rounded-lg border border-solid border-[var(--inv-border)] px-4 py-1.5 text-[1.75rem] font-bold tabular-nums tracking-widest text-[var(--inv-accent)]">
              {elapsedTime}
            </span>
          </div>

          <div className="mb-4 text-sm text-[var(--inv-text-secondary)]">
            <p className="my-1">
              Batch {progress.currentBatch} of {progress.totalBatches}
            </p>
            <p className="my-1">
              Files: {progress.processedFiles} / {progress.totalFiles}
            </p>
          </div>

          <Progress value={progress.percentage} className="mb-3" />

          <p className="m-0 text-sm text-[var(--inv-text-secondary)]">
            {progress.percentage < 100 ? 'Processing…' : 'Finalizing results…'}
          </p>
        </div>
      ) : null}
    </div>
  );

  if (embedded) {
    return content;
  }

  return <OnboardBgWrapper>{content}</OnboardBgWrapper>;
};

export default ProcessFilesContent;
